import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { ProjectActions } from '../actions/project.actions';
import { Project } from '../models/project.model';
import { ProjectService } from 'src/app/services/project.service';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { app_date_time_format } from 'src/app/globals';

export class ProjectStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    projects: Project[]
}

@State<ProjectStateModel>({
    name: 'project_entry',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        projects: []
    }
})
@Injectable()
export class ProjectState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private projectEntryService: ProjectService,        
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<ProjectStateModel>) {   
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                 
                        await this.reloadStateFromStorage()   
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {            
        if (this.storageService.is_initialized) {      
            const state_model: ProjectStateModel = change.currentValue      
            this.storageService.set('projects', JSON.stringify(state_model))    
        }       
    }
    
    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('projects');        
        if (json) {
            const state_model: ProjectStateModel = JSON.parse(json)            
            if (state_model) {                
                this.store.dispatch(new ProjectActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new ProjectActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.projectEntryService.getMine()    
        if (objs != null) {
          const many = Project.buildMany(objs)                   
          this.store.dispatch(new ProjectActions.Set(many))
          return true
        }
        return false
    }
  
    @Selector()
    static getLastUpdate(state: ProjectStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: ProjectStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: ProjectStateModel) {
        return state
    }

    @Selector()
    static getAll(state: ProjectStateModel) {
        return state.projects
    }

    @Selector()
    static getOne(state: ProjectStateModel) {
        return (id: number) => {
            return state.projects.filter(s => s.id === id)
        }
    }

    @Action(ProjectActions.Refresh)
    async refresh({patchState}: StateContext<ProjectStateModel>, {  }:ProjectActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(ProjectActions.Init)
    init({setState, getState}: StateContext<ProjectStateModel>, { payload }:ProjectActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new ProjectActions.Refresh())
    }
    
    @Action(ProjectActions.Set)
    set({setState, getState}: StateContext<ProjectStateModel>, { payload }:ProjectActions.Set) {      
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing, 
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),         
            projects: payload
        })
    }
}
